// `tagsModel.addManualTag`'s `url` arg is normally supplied per-call from
// `order/get_tags`'s response (see AddManualTag.js) - Copilot's resolve
// flow doesn't call `get_tags`, so this is fixed instead.
export const COPILOT_ADD_MANUAL_TAG_URL = 'add_manual_tag';

export const toDisplayProducts = (selectedItems) =>
  (selectedItems || []).map((selected) => ({
    id: selected.id,
    productId: selected.product_id,
    imageUrl: selected.item_image_url || null,
    name: selected.text,
    quantityLabel: selected.description || '',
    count: selected.quantity,
    grievanceOptions: (selected.grievance_level || []).map((level) => ({
      label: level.display_text,
      value: level.grievance_level_slug,
    })),
  }));

// `id` is carried through (not just `label`/`value`) because picking a tag
// in "Add Tags" needs `tag_id` for `add_manual_tag` on "Close Chat" - the
// slug alone isn't enough for that call.
export const toTagOptions = (manualTags) =>
  (manualTags?.manual_tags_list || []).map((tag) => ({
    label: tag.title,
    value: tag.slug,
    id: tag.id,
  }));

/**
 * Unlike "identify_complaint"/"verify_complaint"'s bare "selected_items"
 * (id/product_id/quantity only, needing a lookup against step 1's menu),
 * `evaluate_resolution`'s `recommended_resolution.items`/
 * `alternate_resolution.items` carry full product detail (text/
 * item_image_url/description/unit_cost_text) directly on each item - no
 * cross-referencing needed, so this just reshapes the field names.
 */
export const toResolutionItems = (resolutionItems) =>
  (resolutionItems || []).map((entry) => ({
    id: entry.id,
    productId: entry.product_id,
    imageUrl: entry.item_image_url || null,
    name: entry.text,
    quantityLabel: entry.description || '',
    unitCostText: entry.unit_cost_text || '',
    quantity: entry.quantity,
    resolutionType: entry.resolution_type,
    displayText: entry.display_text,
    pickupEligible: entry.pickup?.eligible ?? false,
    pickupDefaultSelected: entry.pickup?.default_selected ?? false,
  }));

// Refund source choices ("source_account" | "promo_code"); `[]` when absent.
export const toSourceOptions = (options) =>
  (options || []).map((option) => ({
    label: option.display_text,
    value: option.slug,
  }));

// True when every item in both buckets has the same resolution type.
export const isSameRemedy = (recommendedItems, alternateItems) => {
  if (recommendedItems.length === 0 || alternateItems.length === 0) {
    return false;
  }
  const types = new Set(
    [...recommendedItems, ...alternateItems].map((item) => item.resolutionType),
  );
  return types.size === 1;
};

/**
 * `evaluate_resolution.denied_items` - visibility-only, never fed back into
 * `/resolve`'s payload. Branch on `reason.code` (stable), never
 * `reason.message` (free-text, may change wording without a contract bump).
 */
export const toDeniedItems = (deniedItems) =>
  (deniedItems || []).map((entry) => ({
    id: entry.id,
    productId: entry.product_id,
    imageUrl: entry.item_image_url || null,
    name: entry.text,
    quantityLabel: entry.description || '',
    quantity: entry.quantity,
    reasonCode: entry.reason?.code || '',
  }));
